import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, Menu, PanelRight } from 'lucide-react';
import { useLayout } from './context';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@marine/ui/sheet';
import { Button } from '@marine/ui/button';
import {
  SidebarPrimary,
  platformNavItems,
  allTenantModules,
  type NavItem,
} from './sidebar-primary';
import { SidebarSecondary } from './sidebar-secondary';
import { toAbsoluteUrl } from '@/lib/helpers';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@marine/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface HeaderLogoProps {
  portalType?: 'platform' | 'tenant';
  enabledModules?: string[];
}

function getActiveModule(pathname: string, items: NavItem[]): NavItem {
  const match = items.find(
    (item) => item.href !== '#' && item.href !== '/dashboard' && pathname.startsWith(item.href),
  );
  return match ?? items[0];
}

export function HeaderLogo({ portalType = 'platform', enabledModules = ['core'] }: HeaderLogoProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isMobile, sidebarToggle } = useLayout();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const modules: NavItem[] =
    portalType === 'platform'
      ? platformNavItems
      : allTenantModules.filter((item) => item.moduleKey && enabledModules.includes(item.moduleKey));

  const activeModule = getActiveModule(pathname, modules);

  // Close sheet when route changes
  useEffect(() => {
    setIsSheetOpen(false);
  }, [pathname]);

  return (
    <div className="flex border-e border-border items-center gap-2 lg:w-(--sidebar-width)">
      {/* Brand */}
      <div className="flex items-center w-full">
        {/* Logo */}
        <div className="flex items-center justify-center shrink-0 border-e border-border w-(--sidebar-collapsed-width) h-(--header-height) bg-muted">
          <Link to="/dashboard">
            <img
              src={toAbsoluteUrl('/media/app/dms44-logo.png')}
              className="h-9 w-auto"
              alt="DMS 44"
            />
          </Link>
        </div>

        {/* Mobile sidebar toggle */}
        {isMobile && (
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" mode="icon" size="sm" className="ms-5.5">
                <Menu className="size-4" />
              </Button>
            </SheetTrigger>
            <SheetContent
              className="p-0 gap-0 w-[280px] lg:w-(--sidebar-width)"
              side="left"
              close={false}
            >
              <SheetHeader className="p-0 space-y-0" />
              <SheetBody className="flex grow p-0">
                <SidebarPrimary portalType={portalType} enabledModules={enabledModules} />
                <SidebarSecondary />
              </SheetBody>
            </SheetContent>
          </Sheet>
        )}

        {/* Sidebar header */}
        <div className="flex w-full grow items-center justify-between px-5 gap-2.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="inline-flex text-muted-foreground hover:text-foreground px-1.5 -ms-1.5"
              >
                <div className={cn('size-6 flex items-center justify-center rounded-md', activeModule.className)}>
                  <activeModule.icon className="size-4" />
                </div>

                <span className="text-mono text-sm font-medium hidden lg:block">
                  {activeModule.label}
                </span>
                <ChevronsUpDown className="opacity-100" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56"
              side="bottom"
              align="end"
              sideOffset={10}
              alignOffset={-80}
            >
              {modules.map((item) => {
                const isActive = item.label === activeModule.label;
                return (
                  <DropdownMenuItem
                    key={item.label}
                    onClick={() => item.href !== '#' && navigate(item.href)}
                    data-active={isActive}
                  >
                    <div className={cn('size-6 rounded-md flex items-center justify-center', item.className)}>
                      <item.icon className="size-4" />
                    </div>
                    <span className="text-mono text-sm font-medium">{item.label}</span>
                    {isActive && <Check className="ms-auto size-4 text-primary" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sidebar toggle */}
          <Button
            mode="icon"
            variant="ghost"
            onClick={sidebarToggle}
            className="hidden lg:inline-flex text-muted-foreground hover:text-foreground"
          >
            <PanelRight className="-rotate-180 in-data-[sidebar-open=false]:rotate-0 opacity-100" />
          </Button>
        </div>
      </div>
    </div>
  );
}
